//* Necessary for typing in actions and getters.
/* eslint-disable @typescript-eslint/no-use-before-define, no-use-before-define */
//* We would like the errors to appear in the console if they happen.
/* eslint-disable no-console */

import { ActionContext } from 'vuex';
import { defineModule } from 'direct-vuex';
import { cloneDeep, isEqual } from 'lodash';
import { moduleActionContext, moduleGetterContext } from '@/store';
import { IRepository } from '@/interfaces';
import { useFirebaseAuth } from '@/services';
import { BaseAndCustomModuleState, CustomModule, CustomModuleMethods, CustomModuleState, ModuleState } from '@/types';
import * as App from '@/types/app';
import * as Database from '@/types/database';
import { mapAppBaseModelToDatabaseBaseModel, mapDatabaseBaseModelToAppBaseModel, unsafeTypeCast } from '@/utils';

/**
 * Defines a store module based on a type with basic CRUD methods and Firestore syncing.
 * @param useRepository Firestore CRUD methods.
 * @returns A vuex store module.
 */
export const defineFirebaseCollectionStoreModule = <TAppModel extends App.Model, TDatabaseModel extends Database.Model>(
  useRepository: (groupId: string) => IRepository<TDatabaseModel>,
  mapper: {
    databaseModelToAppModel: (model: TDatabaseModel) => TAppModel;
    appModelToDatabaseModel: (model: TAppModel) => TDatabaseModel;
  } = {
    databaseModelToAppModel: mapDatabaseBaseModelToAppBaseModel,
    appModelToDatabaseModel: mapAppBaseModelToDatabaseBaseModel
  }
) => {
  const baseModule = defineModule({
    namespaced: true,
    state: {
      data: {
        firestore: []
      }
    } as ModuleState<TDatabaseModel>,
    getters: {
      get: (...args): TAppModel[] => {
        const { state } = getterContext(args);

        if (mapper) {
          return state.data.firestore.map(item => mapper.databaseModelToAppModel(item));
        }

        // Intentional type casting. If there is no mapper function, then the database model and app model are the same.
        return cloneDeep((state.data.firestore as unknown) as TAppModel[]);
      },
      getById: (...args) => (id: string): TAppModel | undefined => {
        const { state } = getterContext(args);
        const databaseModel = state.data.firestore.find((item: TDatabaseModel) => item.id === id);

        if (!databaseModel) return undefined;
        if (mapper) return mapper.databaseModelToAppModel(databaseModel);

        return unsafeTypeCast<TAppModel>(cloneDeep(databaseModel));
      }
    },
    mutations: {
      UPDATE_FIRESTORE_DATA: (state, payload: TDatabaseModel[]): void => {
        state.data.firestore = payload;
      },
      UPDATE_UNSUBSCRIBE_FROM_FIRESTORE: (state, payload: () => void): void => {
        state.unsubscribeFromFirestore = payload;
      },
      UPDATE_UNSUBSCRIBE_FROM_AUTH: (state, payload: () => void): void => {
        state.unsubscribeFromAuth = payload;
      }
    },
    actions: {
      updateModuleData: (context, payload): void => {
        const { commit } = actionContext(context);

        commit.UPDATE_FIRESTORE_DATA(payload);
      },
      initFirestoreSync: async (context): Promise<void> => {
        const { rootDispatch, dispatch, commit } = actionContext(context);
        const { auth } = useFirebaseAuth();

        const unsubscribeFromAuth = auth.onAuthStateChanged(async user => {
          if (!user) return;

          const groupId = await rootDispatch.groups.getGroupId();
          const operators = useRepository(groupId);
          const unsubscribeFromFirestore = operators.listen(dispatch.updateModuleData);

          commit.UPDATE_UNSUBSCRIBE_FROM_FIRESTORE(unsubscribeFromFirestore);
        });

        commit.UPDATE_UNSUBSCRIBE_FROM_AUTH(unsubscribeFromAuth);
      },
      killFirestoreSync: async (context): Promise<void> => {
        const { state } = actionContext(context);
        state.unsubscribeFromFirestore?.();
        state.unsubscribeFromAuth?.();
      },
      ensureFirestoreListener: (context, expectedPayload: unknown) => async () => {
        const { state, dispatch } = actionContext(context);

        if (!state.unsubscribeFromFirestore) {
          await dispatch.initFirestoreSync();
          return;
        }

        if (!isEqual(state.data.firestore, expectedPayload)) {
          await dispatch.killFirestoreSync();
          await dispatch.initFirestoreSync();
        }
      },
      create: async (context, payload: TAppModel): Promise<void> => {
        const { state, rootDispatch, dispatch } = actionContext(context);

        const groupId = await rootDispatch.groups.getGroupId();
        if (!groupId || !state.data.firestore) return;

        const operators = useRepository(groupId);
        const mappedPayload = mapper?.appModelToDatabaseModel(payload) ?? unsafeTypeCast<TDatabaseModel>(payload);
        const expectedPayload = [...state.data.firestore, mappedPayload];
        const ensureExpectedPayload = await dispatch.ensureFirestoreListener(expectedPayload);

        operators
          .create(mappedPayload)
          .then(() => ensureExpectedPayload())
          .catch(error => {
            console.group('An error occurred while creating a document.');
            console.log('Payload:');
            console.dir(payload);
            console.log('Error:');
            console.log(error);
            console.groupEnd();
          });
      },
      update: async (context, payload: TAppModel): Promise<void> => {
        const { state, dispatch, rootDispatch } = actionContext(context);

        const groupId = await rootDispatch.groups.getGroupId();
        if (!payload.id || !groupId || !state.data.firestore) return;

        const operators = useRepository(groupId);
        const mappedPayload = mapper?.appModelToDatabaseModel(payload) ?? unsafeTypeCast<TDatabaseModel>(payload);
        const expectedPayload = [...state.data.firestore.filter(({ id }) => id !== payload.id), mappedPayload];
        const ensureExpectedPayload = await dispatch.ensureFirestoreListener(expectedPayload);

        operators
          .update(payload.id as string)(mappedPayload)
          .then(() => ensureExpectedPayload())
          .catch(error => {
            console.group('An error occurred while updating a document.');
            console.log('Payload:');
            console.dir(payload);
            console.log('Error:');
            console.log(error);
            console.groupEnd();
          });
      },
      delete: async (context, payload: TAppModel): Promise<void> => {
        const { state, dispatch, rootDispatch } = actionContext(context);

        const groupId = await rootDispatch.groups.getGroupId();
        if (!payload.id || !groupId || !state.data.firestore) return;

        const operators = useRepository(groupId);
        const expectedPayload = state.data.firestore.filter(({ id }) => id !== payload.id);
        const ensureExpectedPayload = await dispatch.ensureFirestoreListener(expectedPayload);

        operators
          .delete(payload.id)
          .then(() => ensureExpectedPayload())
          .catch(error => {
            console.group('An error occurred while deleting a document.');
            console.log('Payload:');
            console.dir(payload);
            console.log('Error:');
            console.log(error);
            console.groupEnd();
          });
      }
    }
  });

  const getterContext = (args: [unknown, unknown, unknown, unknown]) => moduleGetterContext(args, baseModule);
  const actionContext = (context: ActionContext<unknown, unknown>) => moduleActionContext(context, baseModule);

  return baseModule;
};

/**
 * Defines a store module based on a type with basic CRUD and custom provided methods, and Firestore syncing.
 * The provided custom state and methods will overwrite the base module state and methods if they have the same property name.
 * @param useRepository Firestore CRUD methods.
 * @param customMethods Custom state and methods to add to the store module.
 * @returns A vuex store module.
 */
// TODO: Fix the typing on custom modules.
export const defineCustomFirebaseCollectionStoreModule = <
  TAppModel extends App.Model,
  TDatabaseModel extends Database.Model,
  TCustomState extends CustomModuleState,
  TCustomMethods extends CustomModuleMethods<BaseAndCustomModuleState<TDatabaseModel, TCustomState>> = CustomModuleMethods<
    BaseAndCustomModuleState<TDatabaseModel, TCustomState>
  >
>(
  useRepository: (groupId: string) => IRepository<TDatabaseModel>,
  customModule: TCustomMethods & {
    state?: TCustomState;
  },
  mapper?: {
    databaseModelToAppModel: (model: TDatabaseModel) => TAppModel;
    appModelToDatabaseModel: (model: TAppModel) => TDatabaseModel;
  }
): CustomModule<
  typeof baseModule,
  typeof baseModule.state.data & typeof customModule.state,
  {
    getters: typeof baseModule.getters & typeof customModule.getters;
    mutations: typeof baseModule.mutations & typeof customModule.mutations;
    actions: typeof baseModule.actions & typeof customModule.actions;
  }
> => {
  const baseModule = defineFirebaseCollectionStoreModule<TAppModel, TDatabaseModel>(useRepository, mapper);

  baseModule.state = { ...baseModule.state, data: { ...baseModule.state.data, ...customModule.state } };
  baseModule.getters = { ...baseModule.getters, ...customModule.getters };
  baseModule.mutations = { ...baseModule.mutations, ...customModule.mutations };
  baseModule.actions = { ...baseModule.actions, ...customModule.actions };

  return baseModule as CustomModule<
    typeof baseModule,
    typeof baseModule.state.data & typeof customModule.state,
    TCustomMethods & {
      getters: typeof baseModule.getters & typeof customModule.getters;
      mutations: typeof baseModule.mutations & typeof customModule.mutations;
      actions: typeof baseModule.actions & typeof customModule.actions;
    }
  >;
};
